/**
 * Voice-companion session settings: picked once in the prescription builder, kept
 * in localStorage for the whole session, and sent to the backend on the socket's
 * `config` message when an exercise starts.
 *
 * The values live here rather than in each app's own constants because both sides
 * of the flow need the same strings: movexStudio writes them (the builder) and
 * inPerformance reads them (the socket config). Same reason inPerformance already
 * imports `@/zustand/auth-store` across the app boundary.
 *
 * They are session state, not per-exercise parameters, so they are not part of the
 * prescription payload — the socket config is the only place the backend is told.
 */
export const VOICE_COMPANION_MODE = Object.freeze({
  off: 'off',
  log: 'log',
  live: 'live',
});

export const OPENAI_REALTIME_MODEL = Object.freeze({
  mini: 'gpt-realtime-mini-2025-12-15',
  full: 'gpt-realtime',
  v2_1: 'gpt-realtime-2.1',
  v2_1_mini: 'gpt-realtime-2.1-mini',
});

// Storage keys only — the wire names differ per transport: the socket `config`
// sends `voiceCompanionMode`, the realtime token mint sends `modal`. Renaming a
// key here is a localStorage migration, not a protocol change.
export const VOICE_COMPANION_STORAGE_KEYS = Object.freeze({
  voice_companion_mode: 'voice_companion_mode',
  openai_realtime_model: 'openai_realtime_model',
});

export const DEFAULT_VOICE_COMPANION_SETTINGS = Object.freeze({
  voice_companion_mode: VOICE_COMPANION_MODE.off,
  openai_realtime_model: OPENAI_REALTIME_MODEL.mini,
});

export function isVoiceCompanionMode(value) {
  return Object.values(VOICE_COMPANION_MODE).includes(value);
}

export function isOpenAiRealtimeModel(value) {
  return Object.values(OPENAI_REALTIME_MODEL).includes(value);
}

/**
 * What the session picked, or `null` per key when nothing was stored — the caller
 * decides whether to fall back to the defaults (the builder's form does) or to
 * omit the key entirely (the socket config does, so the backend keeps its own
 * default rather than being told a value the author never chose).
 */
export function readVoiceCompanionSettings() {
  const mode = localStorage.getItem(VOICE_COMPANION_STORAGE_KEYS.voice_companion_mode);
  const model = localStorage.getItem(VOICE_COMPANION_STORAGE_KEYS.openai_realtime_model);

  return {
    voice_companion_mode: isVoiceCompanionMode(mode) ? mode : null,
    openai_realtime_model: isOpenAiRealtimeModel(model) ? model : null,
  };
}

export function writeVoiceCompanionSettings(settings) {
  if (isVoiceCompanionMode(settings?.voice_companion_mode)) {
    localStorage.setItem(
      VOICE_COMPANION_STORAGE_KEYS.voice_companion_mode,
      settings.voice_companion_mode
    );
  }

  if (isOpenAiRealtimeModel(settings?.openai_realtime_model)) {
    localStorage.setItem(
      VOICE_COMPANION_STORAGE_KEYS.openai_realtime_model,
      settings.openai_realtime_model
    );
  }
}

/** Called when the user lands back on /home: the session is over. */
export function clearVoiceCompanionSettings() {
  Object.values(VOICE_COMPANION_STORAGE_KEYS).forEach(key => localStorage.removeItem(key));
}
